import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { API_CONFIG, API_LIST } from '../../Constants/ApiDefinations'
import mainApiService from '../../Services'
import { setLoader } from './loader.slice'

/**
 * For future reference, this is the list of all masters that are fetched in fetchAllMasterAPI
const LIST_MASTERS_FETCH_ALL = [
  'AD_LANGUAGES',
  'AFFILIATEPARTNERS',
  'AUDIT_TRAILS',
  'CC_LANGUAGES',
  'CONTENT_CATEGORY',
  'COUNTRIES',
  'DATASOURCE',
  'DOWNSTREAMPLATFORMS',
  'ENTITLEMENT',
  'ENTITLEMENTPACKAGES',
  'ENTITLEMENT_CHANGE_TYPE',
  'GEOPOLICY',
  'LANGUAGES',
  'METACATEGORIES'
]
 */

interface MasterDataState {
  AD_LANGUAGES: any[] | null
  AFFILIATEPARTNERS: any[] | null
  AUDIT_TRAILS: any[] | null
  CC_LANGUAGES: any[] | null
  CONTENT_CATEGORY: any[] | null
  COUNTRIES: any[] | null
  DATASOURCE: any[] | null
  DOWNSTREAMPLATFORMS: any[] | null
  ENTITLEMENT: any[] | null
  ENTITLEMENTPACKAGES: any[] | null
  ENTITLEMENT_CHANGE_TYPE: any[] | null
  GEOPOLICY: any[] | null
  LANGUAGES: any[] | null
  METACATEGORIES: any[] | null
  SEGMENT_ID: any[] | null
  STATES: any[] | null
}

const initialState: MasterDataState = {
  AD_LANGUAGES: null,
  AFFILIATEPARTNERS: null,
  AUDIT_TRAILS: null,
  CC_LANGUAGES: null,
  CONTENT_CATEGORY: null,
  COUNTRIES: null,
  DATASOURCE: null,
  DOWNSTREAMPLATFORMS: null,
  ENTITLEMENT: null,
  ENTITLEMENTPACKAGES: null,
  ENTITLEMENT_CHANGE_TYPE: null,
  GEOPOLICY: null,
  LANGUAGES: null,
  METACATEGORIES: null,
  SEGMENT_ID: null,
  STATES: null,
}

const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    setMasterData: (state: any, action) => {
      const { key, data } = action.payload
      if (!state[key] || state[key] === null) {
        state[key] = data
      }
    },
  },
})

// Async thunk to fetch master data
export const fetchMasterData = createAsyncThunk(
  'masters/fetchMasterData',
  async (config: string[] | any, { dispatch, getState }) => {
    const state = getState() as any
    const cachedData: Record<string, any> = state.masterData

    if (Array.isArray(config)) {
      config = {
        masters: config,
        showLoader: true,
      }
    }
    const { masters, showLoader } = config

    // Split keys into cached and uncached
    const cachedKeys: Record<string, any> = {}
    const keysToFetch: string[] = []
    masters.forEach((key: string) => {
      if (cachedData[key]) {
        if (['GEOPOLICY', 'ADBREAKPOLICIES'].includes(key)) {
          keysToFetch.push(key)
        } else {
          cachedKeys[key] = cachedData[key]
        }
      } else {
        keysToFetch.push(key)
      }
    })

    let fetchedData: Record<string, any> = {}

    if (keysToFetch.length > 0) {
      if (showLoader) {
        dispatch(setLoader(true))
      }
      const requestedData = API_CONFIG(API_LIST.MASTER_APIS.LIST_MASTERS_FETCH_ALL, {
        masters: keysToFetch,
        userDetails: { isFrontendReq: true },
      })

      const response = await mainApiService(requestedData)

      if (response?.data) {
        fetchedData = response.data
        Object.keys(fetchedData).forEach((key) => {
          dispatch(setMasterData({ key, data: fetchedData[key] || [] }))
        })
      }
      if (showLoader) {
        dispatch(setLoader(false))
      }
    }

    return {
      message: 'Masters Data Fetched Successfully',
      responseCode: 200,
      data: { ...cachedKeys, ...fetchedData },
    }
  },
)

export const addEditMasterData = createAsyncThunk(
  'master/addEditMasterData',
  async (payload: any, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData: any = API_CONFIG(API_LIST.MASTER_APIS.ADD_SYSTEM_CONFIG, payload, {
      type: payload.master,
    })
    const data = await mainApiService(requestedData)
    dispatch(setLoader(false))
    return data
  },
)

export const { setMasterData } = masterDataSlice.actions

const masterDataSelector = (state: any) => state.masterData

export const selectMasterData = (key?: keyof MasterDataState) =>
  createSelector(masterDataSelector, (state) => (key ? state[key] : state))

export default masterDataSlice
